import * as TabsPrimitive from "@radix-ui/react-tabs"
import { type VariantProps, cva } from "class-variance-authority"
import { cn } from "../../lib/utils"
import AlertDot from "./AlertDot"

const Tabs = TabsPrimitive.Root

const tabsListVariants = cva("inline-flex items-center", {
  variants: {
    variant: {
      underline: "gap-4 border-b border-border",
      // Page-level nav bar, same look as Team/components/TeamTabs.
      nav: "gap-1 border-b border-border",
      // Boxed switcher: white track, dark inactive labels, tinted active tab.
      // Used on review pages where the tab bar sits on the grey page background.
      segmented: "gap-1 rounded-lg border border-border bg-background p-1",
    },
  },
  defaultVariants: { variant: "underline" },
})

const tabsTriggerVariants = cva(
  "inline-flex items-center whitespace-nowrap transition-colors focus-visible:outline-hidden disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        underline:
          "border-b-2 border-transparent pb-2 text-sm font-medium text-muted-foreground hover:text-foreground focus-visible:text-foreground data-[state=active]:border-primary data-[state=active]:text-foreground",
        nav:
          "-mb-px border-b-2 border-transparent px-3 py-2 text-[13px] text-muted-foreground hover:text-foreground focus-visible:text-foreground data-[state=active]:border-foreground data-[state=active]:font-medium data-[state=active]:text-foreground",
        segmented:
          "group h-9 gap-2 rounded-md px-3.5 text-sm font-medium text-foreground hover:bg-muted focus-visible:bg-muted data-[state=active]:bg-primary/10 data-[state=active]:font-semibold data-[state=active]:text-primary",
      },
    },
    defaultVariants: { variant: "underline" },
  },
)

interface TabsListProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>,
    VariantProps<typeof tabsListVariants> {}

const TabsList = ({ className, variant, ...props }: TabsListProps) => (
  <TabsPrimitive.List className={cn(tabsListVariants({ variant, className }))} {...props} />
)

interface TabsTriggerProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>,
    VariantProps<typeof tabsTriggerVariants> {
  // Red dot after the label: the content behind this tab still needs input.
  dot?: boolean
}

const TabsTrigger = ({ className, variant, dot, children, ...props }: TabsTriggerProps) => (
  <TabsPrimitive.Trigger className={cn(tabsTriggerVariants({ variant, className }))} {...props}>
    {children}
    {dot && <AlertDot className="ml-1.5" />}
  </TabsPrimitive.Trigger>
)

const TabsContent = ({
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>) => (
  <TabsPrimitive.Content
    className={cn("focus-visible:outline-hidden", className)}
    {...props}
  />
)

export { Tabs, TabsList, TabsTrigger, TabsContent, tabsListVariants, tabsTriggerVariants }
